<template>
  <div class="w-full mx-auto h-full flex flex-col bg-[#f7f7f7] border-gray-200 fadeIn">
    <!-- Scrollable Content -->
    <div class="flex-1 min-h-0">
      <div
        class="max-w-4xl mx-auto min-h-full bg-white shadow-sm w-full rounded-lg border-[1px] border-gray-200"
      >
        <!-- Main Content -->
        <div class="p-6">
          <h2 class="text-xl flex items-center">
            <Pencil v-if="isEdit" class="w-5 h-5 mr-2" />
            <BadgePlus v-else class="enter-spin mr-2" />
            {{ isEdit ? 'Edit job posting' : 'New job posting' }}
          </h2>

          <p class="text-gray-500 text-sm mt-2">
            Add a link to the job position and we'll fetch the details to auto-fill the form for you.
          </p>

          <!-- FORM -->
          <Form v-model="form" @submit="handleSubmit" class="mt-4 space-y-4">
            <!-- Job Status (only show when editing) -->
            <FormField v-if="isEdit" name="status_open">
              <FormItem class="flex items-center space-x-4">
                <FormLabel class="flex items-center mt-1.5">
                  <CircleCheck class="w-4 h-4 mr-2" />Is this job posting still open to accepting applications?
                </FormLabel>
                <FormControl>
                  <Switch v-model="form.status_open" />
                </FormControl>
                <span class="text-sm text-gray-500">
                  {{ form.status_open ? 'Yes' : 'No' }}
                </span>
              </FormItem>
            </FormField>

            <!-- Role URL -->
            <FormField name="url">
              <FormItem>
                <FormLabel class="flex items-center">
                  <Link class="text-sm h-4 w-4 mr-2 items-center" /> Job position page URL
                  <span class="text-red-500 ml-0.5">*</span>
                  <span v-if="formErrors.url" class="text-red-500 text-xs ml-auto">
                    {{ formErrors.url?.[0] }}
                    <a
                      v-if="urlErrorRoleId"
                      :href="`/jobs/${urlErrorRoleId}`"
                      class="text-blue-600 hover:text-blue-800 underline"
                    >click here to see</a>
                  </span>
                </FormLabel>
                <FormControl>
                  <div class="flex gap-2 items-center">
                    <Input
                      v-model="form.url"
                      placeholder="https://..."
                      type="url"
                      required
                      :disabled="isAutofillingFromUrl"
                      :class="{ 'border-red-500 focus-visible:ring-red-500': formErrors.url }"
                      @keydown.enter.prevent="handleUrlAutofill"
                    />
                    <Button
                      v-if="!isEdit"
                      type="button"
                      class="shrink-0 h-full bg-cosmicPurple text-white hover:bg-cosmicPurple/80 hover:translate-y-[-1px] transition-all duration-200 font-normal"
                      :disabled="isAutofillingFromUrl || !form.url || form.url.trim().length < 10"
                      @click="handleUrlAutofill"
                    >
                      <Loader2 v-if="isAutofillingFromUrl" class="w-4 h-4 animate-spin" />
                      <Sparkles v-else class="w-4 h-4" />
                      <span class="ml-1.5">{{ isAutofillingFromUrl ? 'Fetching…' : 'Autofill' }}</span>
                    </Button>
                  </div>
                </FormControl>
                <FormDescription v-if="!isEdit" class="text-gray-400 text-xs">
                  Paste the job posting URL and click Autofill to prefill the form.
                  <button
                    v-if="!autofillSuccess && !manualFillEnabled"
                    type="button"
                    class="underline hover:text-gray-600 transition-colors focus:outline-none"
                    @click="manualFillEnabled = true"
                  >Or fill manually</button>
                </FormDescription>
              </FormItem>
            </FormField>


            <!-- Autofill loading state -->
            <div v-if="isAutofillingFromUrl" class="flex flex-col items-center justify-center py-10 gap-3 text-gray-500">
              <Loader2 class="w-8 h-8 animate-spin text-cosmicPurple" />
              <p class="text-sm font-medium">Getting job details&hellip;</p>
              <p class="text-xs text-gray-400">Please wait while we process the job posting page</p>
            </div>

            <!-- Scrape-failed error with manual fill option -->
            <div
              v-if="!isAutofillingFromUrl && scrapeFailedMessage && !manualFillEnabled"
              class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 fadeIn"
            >
              <AlertCircle class="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
              <div>
                <p class="font-medium">{{ scrapeFailedMessage }}</p>
                <p class="text-red-700 font-normal mt-0.5">
                  The URL might be incorrect or the position has closed.
                  Alternatively, you can
                  <button
                    type="button"
                    class="underline font-medium text-red-800 hover:text-red-900 focus:outline-none"
                    @click="manualFillEnabled = true; scrapeFailedMessage = null"
                  >fill the form manually</button>.
                </p>
              </div>
            </div>

            <!-- All other fields: always visible in edit mode, revealed after autofill or manual fill -->
            <template v-if="isEdit || autofillSuccess || manualFillEnabled">

              <!-- Double-check banner (new roles only) -->
              <div
                v-if="!isEdit"
                class="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 fadeIn"
              >
                <ShieldCheck class="w-4 h-4 mt-0.5 shrink-0 text-gray-600" />
                <div v-if="autofillSuccess">
                  <p class="font-medium">Please review the auto-filled data before submitting.</p>
                  <p class="text-gray-700 font-normal mt-0.5">AI extraction may occasionally miss or misread details. Please double-check the filled fields for accuracy.</p>
                </div>
                <div v-else>
                  <p class="font-medium">Fill in the job posting details manually.</p>
                  <p class="text-gray-700 font-normal mt-0.5">Enter the job information below and submit when ready.</p>
                </div>
              </div>

              <!-- Role Name -->
              <FormField name="role_name">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <Laptop class="text-sm h-4 w-4 mr-2 items-center" />Job title
                    <span class="text-red-500 ml-0.5">*</span>
                    <span v-if="formErrors.role_name" class="text-red-500 text-xs ml-auto">{{
                      formErrors.role_name?.[0]
                    }}</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      v-model="form.role_name"
                      placeholder="e.g. Senior Software Engineer"
                      required
                      :class="{ 'border-red-500 focus-visible:ring-red-500': formErrors.role_name }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Company -->
              <EmployerPicker
                v-model="form.employer"
                :initial-employer="initialEmployer"
                @error="(message) => (formErrors.employer = [message])"
              />
              <div v-if="formErrors.employer" class="text-red-500 text-sm mt-1">
                {{ formErrors.employer[0] }}
              </div>

              <!-- Role ID -->
              <FormField v-if="form.employer === 7" name="microsoft_role_id">
                <FormItem>
                  <FormLabel class="mr-2 flex items-center">
                    <FileDigit class="w-4 h-4 mr-2" />Job ID
                    <span class="text-red-500 ml-0.5">*</span>
                    <span v-if="formErrors.microsoft_role_id" class="text-red-500 text-xs ml-auto">{{
                      formErrors.microsoft_role_id?.[0]
                    }}</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      v-model="form.microsoft_role_id"
                      placeholder="Enter Job ID"
                      required
                      :class="{
                        'border-red-500 focus-visible:ring-red-500': formErrors.microsoft_role_id
                      }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Seniority Level -->
              <FormField name="seniority_level">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <ChartColumnBig class="text-sm h-4 w-4 mr-2 items-center" />Seniority Level
                    <span class="text-red-500 ml-0.5">*</span>
                    <span v-if="formErrors.seniority_level" class="text-red-500 text-xs ml-auto">{{
                      formErrors.seniority_level?.[0]
                    }}</span>
                  </FormLabel>
                  <FormControl>
                    <Select v-model="form.seniority_level">
                      <SelectTrigger
                        :class="{
                          'border-red-500 focus-visible:ring-red-500': formErrors.seniority_level
                        }"
                      >
                        <SelectValue placeholder="Select seniority level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="level in experienceLevels"
                            :key="level.id"
                            :value="level.id"
                          >
                            {{ level.label }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Authorization Requirements -->
              <FormField name="work_authorization_requirement">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <EarthLock class="text-sm h-4 w-4 mr-2 items-center" />Local work authorization
                  </FormLabel>
                  <FormControl>
                    <Select v-model="form.work_authorization_requirement">
                      <SelectTrigger>
                        <SelectValue placeholder="e.g. Visa required, etc." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            v-for="requirement in authorizationRequirements"
                            :key="requirement.id"
                            :value="requirement.id"
                          >
                            {{ requirement.label }}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>

              <!-- Locations -->
              <SmartLocationPicker v-model="selectedLocations" />

              <!-- Remote Position -->
              <FormField name="is_remote">
                <FormItem>
                  <div class="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox v-model="form.is_remote" id="is_remote" />
                    </FormControl>
                    <FormLabel
                      for="is_remote"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      This is a remote position
                    </FormLabel>
                  </div>
                </FormItem>
              </FormField>

              <div
                class="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                <hr class="my-6" />
                <p class="mt-2">Optional additional information</p>
                <p class="text-gray-500 text-sm font-normal mt-2">
                  This information will be displayed on the job details page and will help match
                  candidates to the role.
                </p>
              </div>

              <!-- Description -->
              <FormField name="description">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <FileText class="text-sm h-4 w-4 mr-2 items-center" />
                    Description
                    <span v-if="formErrors.description" class="text-red-500 text-xs ml-auto">{{
                      formErrors.description?.[0]
                    }}</span>
                  </FormLabel>
                  <FormDescription class="text-gray-500 text-sm">
                    What does this role do? Key responsibilities and day-to-day tasks.
                  </FormDescription>
                  <FormControl>
                    <Textarea
                      v-model="form.description"
                      placeholder="Describe the role..."
                      :class="{ 'border-red-500 focus-visible:ring-red-500': formErrors.description }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Qualifications -->
              <FormField name="qualifications">
                <FormItem>
                  <FormLabel class="flex items-center">
                    <ListChecks class="text-sm h-4 w-4 mr-2 items-center" />
                    Qualifications
                    <span v-if="formErrors.qualifications" class="text-red-500 text-xs ml-auto">{{
                      formErrors.qualifications?.[0]
                    }}</span>
                  </FormLabel>
                  <FormDescription class="text-gray-500 text-sm">
                    Required skills, experience, and education needed for this role.
                  </FormDescription>
                  <FormControl>
                    <Textarea
                      v-model="form.qualifications"
                      placeholder="List required skills, experience, and education..."
                      :class="{
                        'border-red-500 focus-visible:ring-red-500': formErrors.qualifications
                      }"
                    />
                  </FormControl>
                </FormItem>
              </FormField>

              <!-- Add to my applications (create mode only) -->
              <FormField v-if="!isEdit" name="add_to_my_applications">
                <FormItem>
                  <hr class="my-2" />
                  <div class="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox v-model="addToMyApplications" id="add_to_my_applications" />
                    </FormControl>
                    <FormLabel
                      for="add_to_my_applications"
                      class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Also add to my job applications
                    </FormLabel>
                  </div>
                  <FormDescription class="text-gray-400 text-xs pl-6">
                    Creates a job application entry for you and takes you straight to it.
                  </FormDescription>
                </FormItem>
              </FormField>

            </template>
          </Form>

          <div v-if="isEdit || autofillSuccess || manualFillEnabled" class="flex items-center justify-end mt-6 gap-2">
            <Button
              v-if="isEdit && isGuide"
              variant="destructive"
              class="block min-w-fit w-20 border-[1px] border-red-500 text-red-500 bg-white hover:bg-red-500 hover:text-white hover:translate-y-[-1px] transition-all duration-200 text-xs h-fit py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="isDeleteModalOpen = true"
            >
              Delete job
            </Button>

            <Button
              @click="handleSubmit"
              :disabled="isLoading"
              class="block min-w-fit w-20 bg-cosmicPurple hover:bg-cosmicPurple/80 hover:translate-y-[-1px] transition-all duration-200 text-xs h-fit py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ buttonText }}
            </Button>
          </div>

          <JobDeleteModal
            v-if="isEdit && isDeleteModalOpen && resolvedRoleId"
            :is-open="isDeleteModalOpen"
            :role-id="resolvedRoleId"
            @close="isDeleteModalOpen = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  Link,
  Laptop,
  ChartColumnBig,
  EarthLock,
  BadgePlus,
  FileDigit,
  FileText,
  ListChecks,
  CircleCheck,
  Pencil,
  Loader2,
  ShieldCheck,
  AlertCircle,
  Sparkles
} from 'lucide-vue-next'
import { useRolesStore } from '@/stores/roles'
import { useEmployerStore } from '@/stores/employer'
import { useUserStore } from '@/stores/user'
import experienceLevels from '@/const/experienceLevels'
import authorizationRequirements from '@/const/authorizationRequirements'
import type { AllowedLocation } from '@/types/locations'
import { LocationTypeEnum } from '@/types/locations'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { autofillFromUrl } from '@/utils/autofillFromUrl'
import SmartLocationPicker from '@/components/common/SmartLocationPicker.vue'
import EmployerPicker from '@/components/common/EmployerPicker.vue'
import { useToast } from '@/components/ui/toast/use-toast'
import { Textarea } from '@/components/ui/textarea'
import JobDeleteModal from '@/components/jobs/JobDeleteModal.vue'
import { useAuth } from '@/composables/useAuth'
import { useApplicationsStore } from '@/stores/applications'

interface Props {
  roleId?: string | string[]
  isEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEdit: false
})

const { toast } = useToast()

const router = useRouter()
const rolesStore = useRolesStore()
const employerStore = useEmployerStore()
const userStore = useUserStore()
const applicationsStore = useApplicationsStore()
const isLoading = ref(false)
const addToMyApplications = ref(false)
const isAutofillingFromUrl = ref(false)
const autofillSuccess = ref(false)
const manualFillEnabled = ref(false)
const scrapeFailedMessage = ref<string | null>(null)
const urlErrorRoleId = ref<number | null>(null)
const isDeleteModalOpen = ref(false)

// Snapshot of form state at the moment autofill completes, used to detect
// whether the user edited any field before submitting.
type FormSnapshot = {
  role_name: string
  employer: number | null
  seniority_level: string | null
  work_authorization_requirement: string | null
  microsoft_role_id: string
  is_remote: boolean
  description: string
  qualifications: string
  status_open: boolean
  locationIds: number[]
}
const autofillSnapshot = ref<FormSnapshot | null>(null)
const wasEditedAfterAutofill = ref(false)
const { isGuide } = useAuth()

const resolvedRoleId = computed(() => {
  const rawId = Array.isArray(props.roleId) ? props.roleId[0] : props.roleId
  if (rawId) return String(rawId)
  const selected = rolesStore.selectedRole?.id
  return selected ? String(selected) : ''
})

// Employer set by URL autofill — kept so the picker can display it even if it
// isn't in the currently-loaded paginated options list.
const autofillEmployer = ref<{ id: number; name: string; logo: string | null } | null>(null)

const initialEmployer = computed(() => {
  // Edit mode: derive from the cached role
  const role = rolesStore.selectedRole as any
  if (role) {
    const id = typeof role.employer === 'number' ? role.employer : role.employer_details?.id
    if (id) {
      return {
        id,
        name: role.employer_details?.name || '',
        logo: role.employer_details?.logo || null
      }
    }
  }
  // Create mode after autofill: use the autofilled employer so the picker shows
  // the name even if it isn't in the first paginated page of options.
  return autofillEmployer.value
})

const buttonText = computed(() => {
  if (isLoading.value) {
    return props.isEdit ? 'Updating...' : 'Submitting...'
  }
  return props.isEdit ? 'Update' : 'Submit'
})

// New location management state
const selectedLocations = ref<AllowedLocation[]>([])

// Separate form and computed locations
const form = ref({
  role_name: '',
  employer: null as number | null,
  seniority_level: null,
  url: '',
  work_authorization_requirement: null,
  microsoft_role_id: '',
  is_remote: false,
  description: '',
  qualifications: '',
  status_open: true
})

// Add form errors state
const formErrors = ref<Record<string, string[]>>({})

// When the URL is edited, collapse back to URL-only state
watch(
  () => form.value.url,
  () => {
    if (!props.isEdit) {
      autofillSuccess.value = false
      scrapeFailedMessage.value = null
      manualFillEnabled.value = false
      autofillEmployer.value = null
      urlErrorRoleId.value = null
      autofillSnapshot.value = null
      wasEditedAfterAutofill.value = false
      formErrors.value = {}
    }
  }
)

// Compute locations separately
const formattedLocations = computed(() => selectedLocations.value.map((loc) => loc.id))

// Helper to sync form from a role object
// eslint-disable-next-line complexity
function populateFromRole(role: any) {
  form.value = {
    role_name: role.role_name || '',
    employer:
      typeof role.employer === 'number' ? role.employer : (role.employer_details?.id ?? null),
    seniority_level: role.seniority_level || null,
    url: role.url || '',
    work_authorization_requirement: role.work_authorization_requirement || null,
    microsoft_role_id: role.microsoft_role_id || '',
    is_remote: role.is_remote || false,
    description: role.description || '',
    qualifications: role.qualifications || '',
    status_open: role.status_open !== undefined ? role.status_open : true
  }

  // Seed employer option so EmployerPicker can display name/logo immediately
  const employerId =
    typeof role.employer === 'number' ? role.employer : (role.employer_details?.id ?? null)
  if (employerId && role.employer_details?.name) {
    const exists = employerStore.employerOptions.some((e: any) => e.id === employerId)
    if (!exists) {
      employerStore.employerOptions = [
        ...employerStore.employerOptions,
        {
          id: employerId,
          name: role.employer_details.name,
          logo: role.employer_details.logo || null,
          url: role.employer_details.url || null,
          is_active: true,
          default_referrer: ''
        }
      ]
    }
  }

  // Prefer locations_details for initializing SmartLocationPicker
  if (
    role.locations_details &&
    Array.isArray(role.locations_details) &&
    role.locations_details.length > 0
  ) {
    const mapped: AllowedLocation[] = role.locations_details.map((loc: any) => ({
      id: Number(loc.id),
      city: null,
      city_details: null,
      country: null,
      country_details: null,
      location_type: LocationTypeEnum.CITY as any,
      region: null,
      us_state: null,
      location_name: String(loc.location_name || '')
    }))
    selectedLocations.value = mapped
  } else if (role.locations && Array.isArray(role.locations) && role.locations.length > 0) {
    // Fallback: map bare IDs if details are missing
    const mapped: AllowedLocation[] = role.locations.map((id: number | string) => ({
      id: Number(id),
      city: null,
      city_details: null,
      country: null,
      country_details: null,
      location_type: LocationTypeEnum.CITY as any,
      region: null,
      us_state: null,
      location_name: ''
    }))
    selectedLocations.value = mapped
  }
}

// Immediately populate from cached selectedRole to avoid empty initial render
watch(
  () => rolesStore.selectedRole,
  (role) => {
    if (!props.isEdit) return
    const rawId = Array.isArray(props.roleId) ? props.roleId[0] : props.roleId
    if (!rawId) return
    if (role && role.id === Number(rawId)) {
      populateFromRole(role)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await Promise.all([employerStore.fetchInitialData(), userStore.fetchCurrentUser()])
  // Do not fetch role here; parent prefetches and watcher above populates the form
})

// eslint-disable-next-line complexity
async function handleSubmit() {
  const user = userStore.currentUser

  // Prevent submit if URL already exists in the system
  if (!props.isEdit && urlErrorRoleId.value) {
    toast({
      variant: 'destructive',
      title: 'Cannot Submit',
      description: 'Please use a different URL or view the existing job posting'
    })
    return
  }

  // When creating a new role and the URL wasn't validated via autofill,
  // check uniqueness before submitting.
  if (!props.isEdit && !autofillSuccess.value && form.value.url?.trim()) {
    try {
      isLoading.value = true
      const { data } = await api.get(API_ROUTES.CHECK_URL_UNIQUE, {
        params: { url: form.value.url.trim() }
      })
      if (data.role_id) {
        urlErrorRoleId.value = data.role_id
        formErrors.value.url = ['Job posting already exists']
        toast({
          variant: 'destructive',
          title: 'Cannot Submit',
          description: 'Please use a different URL or view the existing job posting'
        })
        return
      }
    } catch {
      // Non-fatal — let the backend catch it on create if the check fails
    } finally {
      isLoading.value = false
    }
  }

  try {
    isLoading.value = true
    formErrors.value = {} // Clear previous errors

    // Handle new employer creation if needed
    if (typeof form.value.employer === 'string') {
      const newEmployer = await employerStore.checkAndCreateEmployer(form.value.employer)
      form.value.employer = newEmployer.id
    }

    // Transform form data to match backend requirements
    const roleData = {
      author: Number(user?.id),
      url: form.value.url,
      role_name: form.value.role_name,
      seniority_level: form.value.seniority_level,
      employer: form.value.employer,
      work_authorization_requirement: form.value.work_authorization_requirement,
      locations: formattedLocations.value,
      status_open: form.value.status_open,
      microsoft_role_id: form.value.microsoft_role_id,
      is_remote: form.value.is_remote,
      description: form.value.description,
      qualifications: form.value.qualifications,
      // null  → created manually (no autofill)
      // true  → autofilled and submitted without changes
      // false → autofilled but user modified at least one field
      is_autofilled: autofillSnapshot.value !== null ? !wasEditedAfterAutofill.value : null
    }

    let response
    if (props.isEdit && props.roleId) {
      const updateId = Array.isArray(props.roleId) ? props.roleId[0] : props.roleId
      response = await rolesStore.updateRole(String(updateId), roleData)
    } else {
      response = await rolesStore.createRole(roleData)
    }

    const roleId = response.id || props.roleId

    // If the user wants to immediately track this as their own application, create it
    let newApplicationId: number | null = null
    if (!props.isEdit && addToMyApplications.value) {
      try {
        const appResponse = await applicationsStore.createApplication({
          open_role: Number(roleId)
        })
        newApplicationId = appResponse.id
      } catch {
        // Non-fatal — fall back to redirecting to the job page
      }
    }

    // Show success toast
    toast({
      title: 'Success',
      description: props.isEdit
        ? 'Job posting updated successfully!'
        : newApplicationId
          ? 'Job posting created and added to your applications!'
          : 'Job posting submitted successfully!'
    })

    setTimeout(async () => {
      await rolesStore.fetchRoles()
      if (newApplicationId) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push(`/applications/${newApplicationId}`)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.push(`/jobs/${roleId}`)
      }
    }, 500)
  } catch (error: any) {
    console.error(`Failed to ${props.isEdit ? 'update' : 'create'} role:`, error)

    // Handle validation errors
    if (error && typeof error === 'object' && !Array.isArray(error)) {
      formErrors.value = error
      // Show error toast with validation message
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please check the form for errors'
      })
    } else {
      // Show general error toast
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to ${props.isEdit ? 'update' : 'submit'} job posting`
      })
    }
  } finally {
    isLoading.value = false
  }
}

// Handle URL autofill (triggered by button click or Enter key)
// eslint-disable-next-line complexity
const handleUrlAutofill = async () => {
  if (props.isEdit) return
  // Don't fire if URL is empty or too short to be a real URL
  if (!form.value.url || form.value.url.trim().length < 10) return

  isAutofillingFromUrl.value = true
  scrapeFailedMessage.value = null
  urlErrorRoleId.value = null
  try {
    const result = await autofillFromUrl(form.value.url)

    if (result.hasError && result.errorMessage) {
      if (result.errorType === 'scrape_failed') {
        scrapeFailedMessage.value = result.errorMessage
        delete formErrors.value.url
      } else {
        formErrors.value.url = [result.errorMessage]
        if (result.errorType === 'not_unique' && result.roleId) {
          urlErrorRoleId.value = result.roleId
        }
      }
      return
    }

    scrapeFailedMessage.value = null
    delete formErrors.value.url

    const autofillData = result?.autofillData
    if (!autofillData) {
      return
    }

    autofillSuccess.value = true

    form.value.role_name = autofillData.role_name || form.value.role_name
    form.value.employer = autofillData.employer
    if (autofillData.employer && autofillData.employer_details?.name) {
      const employerHint = {
        id: autofillData.employer,
        name: autofillData.employer_details.name,
        logo: autofillData.employer_details.logo || null,
        url: autofillData.employer_details.url || null
      }
      autofillEmployer.value = employerHint
      employerStore.ensureEmployerInOptions(employerHint)
    }
    form.value.seniority_level = autofillData.seniority_level as any
    form.value.is_remote = autofillData.is_remote
    form.value.description = autofillData.description || ''
    form.value.qualifications = autofillData.qualifications || ''
    form.value.microsoft_role_id = autofillData.microsoft_role_id || ''
    form.value.status_open = autofillData.status_open

    const matchedLocations: AllowedLocation[] = (autofillData.locations_details || []).map(
      (location: any) => ({
        id: Number(location.id),
        city: location.city ?? null,
        city_details: location.city_details ?? null,
        country: location.country ?? null,
        country_details: location.country_details ?? null,
        location_type: location.location_type as LocationTypeEnum,
        region: location.region ?? null,
        us_state: location.us_state ?? null,
        location_name: location.location_name || ''
      })
    )

    const createdSuggestedLocations: AllowedLocation[] = []
    for (const suggestion of autofillData.suggested_locations || []) {
      try {
        const createData = suggestion.create_data || {}
        const response = await api.post(API_ROUTES.CREATE_JOB_LOCATION, createData)
        createdSuggestedLocations.push(response.data as AllowedLocation)
      } catch (error) {
        console.error('Failed to create suggested location:', suggestion, error)
      }
    }

    const mergedLocations = [...matchedLocations, ...createdSuggestedLocations]
    const uniqueLocations = mergedLocations.filter(
      (location, index, self) => self.findIndex((item) => item.id === location.id) === index
    )
    selectedLocations.value = uniqueLocations

    // Capture a snapshot of the autofilled state so we can detect edits at submit time.
    // Must run after all fields and locations are set.
    wasEditedAfterAutofill.value = false
    autofillSnapshot.value = {
      role_name: form.value.role_name,
      employer: form.value.employer,
      seniority_level: form.value.seniority_level,
      work_authorization_requirement: form.value.work_authorization_requirement,
      microsoft_role_id: form.value.microsoft_role_id,
      is_remote: form.value.is_remote,
      description: form.value.description,
      qualifications: form.value.qualifications,
      status_open: form.value.status_open,
      locationIds: uniqueLocations.map((l) => l.id).sort()
    }
  } finally {
    isAutofillingFromUrl.value = false
  }
}

// After a snapshot is taken, watch for any field change and flag it.
watch(
  [
    () => form.value.role_name,
    () => form.value.employer,
    () => form.value.seniority_level,
    () => form.value.work_authorization_requirement,
    () => form.value.microsoft_role_id,
    () => form.value.is_remote,
    () => form.value.description,
    () => form.value.qualifications,
    () => form.value.status_open,
    () => selectedLocations.value.map((l) => l.id).join(',')
  ],
  () => {
    if (!autofillSnapshot.value || wasEditedAfterAutofill.value) return
    const snap = autofillSnapshot.value
    const currentLocationIds = selectedLocations.value.map((l) => l.id).sort()
    const changed =
      form.value.role_name !== snap.role_name ||
      form.value.employer !== snap.employer ||
      form.value.seniority_level !== snap.seniority_level ||
      form.value.work_authorization_requirement !== snap.work_authorization_requirement ||
      form.value.microsoft_role_id !== snap.microsoft_role_id ||
      form.value.is_remote !== snap.is_remote ||
      form.value.description !== snap.description ||
      form.value.qualifications !== snap.qualifications ||
      form.value.status_open !== snap.status_open ||
      currentLocationIds.join(',') !== snap.locationIds.join(',')
    if (changed) wasEditedAfterAutofill.value = true
  }
)

</script>

<style scoped>
.transition-opacity {
  transition-property: opacity;
}
</style>
